import { useState, useEffect, useCallback } from "react";
import { Box, Text, Button, HStack } from "@chakra-ui/react";
import { motion, AnimatePresence } from "framer-motion";

// ── Config ─────────────────────────────────────────────────────────
const SAFFRON = "#E07B1A";
const SAFFRON_DARK = "#854F0B";
const SAFFRON_PALE = "#FAEEDA";
const DEEP = "#412402";
const STORAGE_KEY = "temple_diya_lit"; // one vote per browser/device

const MotionBox = motion(Box);

/**
 * Public vote widget for the Expenditures page.
 * Devotees "light a diya" as their vote to request early reveal of
 * totals. No count is ever shown here — only the admin dashboard
 * can see it (via /api/admin/votes/count).
 *
 * Props:
 *   onVote — async function() called when the user votes.
 *            Should call castVote() from api.js.
 */
export default function VoteToReveal({ onVote }) {
  const [hasVoted, setHasVoted] = useState(false);
  const [voting, setVoting] = useState(false);
  const [showToast, setShowToast] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY) === "true") setHasVoted(true);
  }, []);

  const handleVote = useCallback(async () => {
    if (hasVoted || voting) return;
    setVoting(true);
    try {
      await onVote();
      setHasVoted(true);
      localStorage.setItem(STORAGE_KEY, "true");
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3500);
    } catch {
      // Silent fail is fine here — user can just tap again.
    } finally {
      setVoting(false);
    }
  }, [hasVoted, voting, onVote]);

  return (
    <>
      <MotionBox
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        bgGradient={`linear(to-b, ${SAFFRON_PALE}, #FBF3E3)`}
        border="1.5px solid"
        borderColor="orange.300"
        borderRadius="24px"
        p={{ base: 5, md: 7 }}
        mb={6}
        textAlign="center"
        position="relative"
        overflow="hidden"
      >
        {/* Temple motif row */}
        <Text
          fontSize={{ base: "md", md: "lg" }}
          letterSpacing="10px"
          color="orange.300"
          mb={3}
          userSelect="none"
        >
          ॐ 🪷 🔔 🪷 ॐ
        </Text>

        <Text
          fontSize={{ base: "md", md: "lg" }}
          fontWeight={800}
          color={DEEP}
          mb={2}
        >
          Light a diya for the temple
        </Text>

        <Text
          fontSize="sm"
          color={SAFFRON_DARK}
          lineHeight={1.65}
          maxW="420px"
          mx="auto"
          mb={6}
        >
          Every sacred lamp carries a devotee's wish to behold the temple's
          divine construction. When many lamps are offered, its journey shall be
          revealed.
        </Text>

        {/* Diya */}
        <MotionBox
          display="flex"
          justifyContent="center"
          mb={5}
          animate={hasVoted ? { scale: [1, 1.12, 1] } : {}}
          transition={{
            duration: 1.6,
            repeat: hasVoted ? Infinity : 0,
            repeatType: "loop",
          }}
        >
          <Box
            as="span"
            fontSize={{ base: "48px", md: "56px" }}
            cursor={hasVoted ? "default" : "pointer"}
            userSelect="none"
            filter={hasVoted ? "none" : "grayscale(1) opacity(0.45)"}
            transition="filter 0.4s ease"
            onClick={handleVote}
            role="img"
            aria-label={hasVoted ? "Diya lit" : "Light diya"}
          >
            🪔
          </Box>
        </MotionBox>

        {/* Action button */}
        <AnimatePresence mode="wait">
          {hasVoted ? (
            <motion.div
              key="voted"
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 400, damping: 22 }}
            >
              <Button
                isDisabled
                colorScheme="orange"
                borderRadius="full"
                px={7}
                size={{ base: "md", md: "lg" }}
                bg={SAFFRON}
                color="white"
                _disabled={{ opacity: 1, cursor: "default", bg: SAFFRON }}
              >
                Diya lit 🙏
              </Button>
            </motion.div>
          ) : (
            <motion.div key="unvoted" whileTap={{ scale: 0.95 }}>
              <Button
                colorScheme="orange"
                borderRadius="full"
                px={7}
                size={{ base: "md", md: "lg" }}
                bg={SAFFRON}
                color="white"
                _hover={{ bg: SAFFRON_DARK }}
                isLoading={voting}
                loadingText="Lighting…"
                onClick={handleVote}
              >
                Light your diya
              </Button>
            </motion.div>
          )}
        </AnimatePresence>

        <Text fontSize="11px" color="gray.400" mt={4}>
          One diya per devotee, per device 🙏
        </Text>
      </MotionBox>

      {/* Confirmation toast */}
      <AnimatePresence>
        {showToast && (
          <MotionBox
            initial={{ opacity: 0, y: 20, x: "-50%" }}
            animate={{ opacity: 1, y: 0, x: "-50%" }}
            exit={{ opacity: 0, y: 20, x: "-50%" }}
            transition={{ duration: 0.35 }}
            position="fixed"
            bottom="24px"
            left="50%"
            bg={DEEP}
            color={SAFFRON_PALE}
            px={5}
            py={3}
            borderRadius="14px"
            boxShadow="0 8px 24px rgba(0,0,0,0.28)"
            zIndex={999}
            fontSize="sm"
          >
            <HStack spacing={2}>
              <Text>🙏</Text>
              <Text>
                Your diya now joins the prayers of fellow devotees. 🪔
              </Text>
            </HStack>
          </MotionBox>
        )}
      </AnimatePresence>
    </>
  );
}
